import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Bell, X, CheckCircle2, AlertTriangle, AlertCircle, Info, Sparkles,
  CheckCheck, Loader2,
} from "lucide-react";
import {
  listNotifications, markNotificationRead, markAllNotificationsRead,
} from "../lib/s3Api";
import { useRole } from "../lib/role.jsx";
import { cn } from "../lib/utils";

const POLL_MS = 60_000;
const SEVERITY_META = {
  info:     { icon: Info,          cls: "text-info border-info/40 bg-info/10" },
  warning:  { icon: AlertTriangle, cls: "text-warning border-warning/40 bg-warning/10" },
  critical: { icon: AlertCircle,   cls: "text-critical border-critical/40 bg-critical/10" },
};

// -----------------------------------------------------------------------------
// Replaces the static bell in AppShell with a live-polling dropdown.
// Role-filtered via /api/s3/notifications?role=<id>. Dedupes server-side
// so a stuck connection raises ONE row with an occurrence counter.
// -----------------------------------------------------------------------------
export default function NotificationDropdown() {
  const { role } = useRole();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ notifications: [], unread_count: 0 });
  const [loading, setLoading] = useState(false);
  const [marking, setMarking] = useState(false);
  const panelRef = useRef(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const d = await listNotifications({ role: role?.id, limit: 30 });
      setData(d);
    } catch {
      /* silent — bell is a non-critical nicety */
    } finally {
      setLoading(false);
    }
  }, [role?.id]);

  // Initial fetch + polling.
  useEffect(() => {
    if (!role?.id) return;
    load();
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, [role?.id, load]);

  // Click-outside close.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  async function onRead(id) {
    try { await markNotificationRead(id); } catch { /* ignore */ }
    await load();
  }
  async function onMarkAll() {
    if (data.unread_count === 0) return;
    setMarking(true);
    try { await markAllNotificationsRead(role?.id); } catch { /* ignore */ }
    await load();
    setMarking(false);
  }

  const unread = data.unread_count || 0;
  return (
    <div className="relative" ref={panelRef}>
      <button data-testid="notif-button"
              onClick={() => setOpen(v => !v)}
              className="relative h-9 w-9 inline-flex items-center justify-center border border-border bg-surface-2 hover:bg-surface rounded-sm">
        <Bell size={15} />
        {unread > 0 && (
          <span data-testid="notif-dot"
                className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 rounded-full bg-critical text-primary-fg text-[9px] font-bold inline-flex items-center justify-center">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div data-testid="notif-dropdown"
             className="absolute right-0 top-11 w-[380px] max-h-[520px] bg-surface border border-border rounded-sm shadow-2xl z-50 flex flex-col animate-fade-in">
          <div className="px-3 py-2.5 border-b border-border flex items-center gap-2">
            <Bell size={13} className="text-primary" />
            <span className="text-[12px] font-semibold">Notifications</span>
            <span className="text-[10.5px] text-muted">{unread} unread · {data.notifications.length} total</span>
            <div className="ml-auto flex items-center gap-1">
              {unread > 0 && (
                <button data-testid="notif-mark-all" onClick={onMarkAll} disabled={marking}
                        className="inline-flex items-center gap-1 px-2 py-0.5 text-[10.5px] border border-border hover:border-primary hover:text-primary rounded-sm disabled:opacity-40">
                  {marking ? <Loader2 size={10} className="animate-spin" /> : <CheckCheck size={10} />}
                  Mark all read
                </button>
              )}
              <button data-testid="notif-close" onClick={() => setOpen(false)}
                      className="h-6 w-6 inline-flex items-center justify-center hover:bg-surface-2 rounded-sm">
                <X size={11} />
              </button>
            </div>
          </div>

          <div className="overflow-y-auto flex-1">
            {loading && data.notifications.length === 0 ? (
              <div className="py-10 text-center text-muted text-[12px]">
                <Loader2 size={14} className="inline animate-spin mr-1.5" /> Loading…
              </div>
            ) : data.notifications.length === 0 ? (
              <div className="py-10 text-center text-muted">
                <Sparkles size={18} className="mx-auto mb-2 opacity-60" />
                <div className="text-[12px] font-medium text-fg">All clear.</div>
                <div className="text-[11px] mt-1">No notifications in your inbox.</div>
              </div>
            ) : (
              data.notifications.map(n => <Row key={n.id} n={n} onRead={onRead} />)
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ n, onRead }) {
  const meta = SEVERITY_META[n.severity] || SEVERITY_META.info;
  const Icon = meta.icon;
  const unread = !n.read_at;
  return (
    <div data-testid={`notif-row-${n.id}`}
         className={cn("px-3 py-2.5 border-b border-border/60 flex items-start gap-2.5 hover:bg-surface-2/40 transition-colors",
           unread ? "bg-surface-2/20" : "opacity-70")}>
      <div className={cn("w-6 h-6 shrink-0 rounded-sm flex items-center justify-center border mt-0.5", meta.cls)}>
        <Icon size={12} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-[12px] font-medium leading-snug">{n.title}</span>
          {n.occurrences > 1 && (
            <span className="text-[10px] px-1 py-px rounded-sm bg-surface-2 text-muted font-mono-tabular">×{n.occurrences}</span>
          )}
        </div>
        <div className="text-[11px] text-muted leading-snug mt-0.5">{n.body}</div>
        <div className="text-[10px] text-muted mt-1 font-mono-tabular">
          {(n.updated_at || n.created_at || "").slice(0, 16).replace("T", " ")} UTC
        </div>
      </div>
      {unread && (
        <button data-testid={`notif-read-${n.id}`} onClick={() => onRead(n.id)}
                title="Mark as read"
                className="shrink-0 w-6 h-6 inline-flex items-center justify-center text-muted hover:text-success hover:bg-success/10 rounded-sm">
          <CheckCircle2 size={12} />
        </button>
      )}
    </div>
  );
}
